import { Configuration, FrontendApi } from "@ory/client";
import { ORY_BASE_PATH, ORY_URL } from "../common/config";
import { t } from "i18next";
import { showError } from "utils/index";
import { hardRedirect } from "./navigation";
import {
  beginUnauthorizedTeardown,
  clearIdentityState,
  endUnauthorizedTeardown,
  isLogoutInProgress,
} from "./session";

const ORY_TIMEOUT_MS = 10000;

export const ory = new FrontendApi(
  new Configuration({
    basePath: ORY_BASE_PATH,
    baseOptions: {
      withCredentials: true,
      timeout: ORY_TIMEOUT_MS,
    },
  })
);

export const isSessionAlreadyEnded = (error: unknown): boolean => {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return false;
  }

  const { response } = error as { response?: { status?: number } };

  return response?.status === 401;
};

export const redirectToOryLogin = (): void => {
  hardRedirect(`${ORY_URL}/ui/login`);
};

export const isOidcSession = async (): Promise<boolean> => {
  try {
    const { data: session } = await ory.toSession();

    return Boolean(
      session.authentication_methods?.some(method => method.method === "oidc")
    );
  } catch {
    return true;
  }
};

export const endOrySession = async (): Promise<void> => {
  try {
    const { data: flow } = await ory.createBrowserLogoutFlow();

    await ory.updateLogoutFlow({
      token: flow.logout_token,
    });
  } catch (error) {
    if (isSessionAlreadyEnded(error)) {
      return;
    }

    throw error;
  }
};

export const handleUnauthorized = async (): Promise<void> => {
  if (!beginUnauthorizedTeardown()) {
    return;
  }

  showError(t("common:sessionExpired"));

  try {
    try {
      await endOrySession();
    } catch (error) {
      console.error(error);
    }

    await clearIdentityState();
  } finally {
    endUnauthorizedTeardown();
  }

  if (isLogoutInProgress()) {
    return;
  }

  hardRedirect("/");
};
